import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const RE = /\[(slot|include|brand|layout):([a-zA-Z0-9_.\-]+)\]/g;
const LAYOUT_FRAGMENTS = {
  "hero.fullscreen":   "",
  "grid.2col": "", "grid.3col": "", "grid.4col": "", "grid.5col": "",
  "slider.horizontal": "", "pattern.interrupt": "",
  "video.fullwidth": "", "cta.fullscreen": "",
};

/** Hook that lazy-loads the business's slot + snippet pack and returns a
 *  ``resolve(content)`` function. Caches per session. Falls back to the
 *  original string if the pack hasn't loaded yet — so the UI never blanks.
 */
export default function useHustleSlots() {
  const [pack, setPack] = useState({ slots: {}, snippets: {}, brand: {}, ready: false });

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/builder/slots").catch(() => ({ data: { slots: [] } })),
      api.get("/builder/snippets").catch(() => ({ data: { snippets: [] } })),
    ]).then(([s, sn]) => {
      if (!mounted) return;
      const slots = {};
      const brand = {};
      (s.data?.slots || []).forEach((d) => {
        slots[d.slot_key] = d.placeholder_value || "";
        if (d.slot_key.startsWith("brand.")) brand[d.slot_key] = d.placeholder_value || "";
      });
      const snippets = {};
      (sn.data?.snippets || []).forEach((d) => {
        if (d.is_active !== false) snippets[d.snippet_key] = d.snippet_content || "";
      });
      setPack({ slots, snippets, brand, ready: true });
    });
    return () => { mounted = false; };
  }, []);

  const resolve = useCallback((s) => {
    if (!s) return s;
    let out = String(s);
    for (let i = 0; i < 4; i += 1) {
      const before = out;
      out = out.replace(RE, (m, kind, key) => {
        if (kind === "slot") return pack.slots[key] ?? m;
        if (kind === "include") return pack.snippets[key] ?? m;
        if (kind === "brand") return pack.brand[`brand.${key}`] ?? m;
        if (kind === "layout") return LAYOUT_FRAGMENTS[key] ?? m;
        return m;
      });
      if (out === before) break;
    }
    return out;
  }, [pack]);

  return { resolve, ready: pack.ready, pack };
}
